import { useCallback, useRef, useSyncExternalStore } from 'react';
import type { FilterBarController, FilterBarSnapshot, FilterValues } from '../types';

/**
 * Reactive read of the whole bar snapshot. The component re-renders on every
 * state transition (value, phase, visibility, dirty changes). For
 * fine-grained subscriptions use `useFilterBarSelector`.
 */
export function useFilterBarSnapshot<T extends FilterValues = FilterValues>(
  controller: FilterBarController<T>,
): FilterBarSnapshot<T> {
  return useSyncExternalStore(controller.subscribe, controller.getState, controller.getState);
}

/**
 * Fine-grained reactive read: re-renders only when `selector(snapshot)`
 * changes per `isEqual` (default `Object.is`).
 *
 * Selecting snapshot SLOTS is free of extra plumbing — `values`, `phases`,
 * `errors`, and `visibleFields` keep their identity while their content is
 * unchanged, so `s => s.values.plan` or `s => s.phases.plan` just works.
 * A selector that BUILDS a fresh object per call needs an `isEqual` (e.g. a
 * shallow comparator) to avoid re-rendering on every transition.
 *
 * Inline selectors are safe — the hook re-selects when the selector identity
 * changes and keeps the previous result reference when `isEqual` says equal.
 */
export function useFilterBarSelector<T extends FilterValues, TSelected>(
  controller: FilterBarController<T>,
  selector: (snapshot: FilterBarSnapshot<T>) => TSelected,
  isEqual: (a: TSelected, b: TSelected) => boolean = Object.is,
): TSelected {
  const selectorRef = useRef(selector);
  selectorRef.current = selector;
  const isEqualRef = useRef(isEqual);
  isEqualRef.current = isEqual;

  const cacheRef = useRef<{
    snapshot: FilterBarSnapshot<T>;
    selector: typeof selector;
    selected: TSelected;
  } | null>(null);

  // getSnapshot for useSyncExternalStore: must return the SAME reference until
  // something actually changed — memoized per (snapshot, selector) pair, with
  // isEqual preserving the previous result's identity across re-selections.
  const getSelected = useCallback((): TSelected => {
    const snapshot = controller.getState();
    const select = selectorRef.current;
    const cache = cacheRef.current;
    if (cache && cache.snapshot === snapshot && cache.selector === select) {
      return cache.selected;
    }
    const next = select(snapshot);
    if (cache && isEqualRef.current(cache.selected, next)) {
      cacheRef.current = { snapshot, selector: select, selected: cache.selected };
      return cache.selected;
    }
    cacheRef.current = { snapshot, selector: select, selected: next };
    return next;
  }, [controller]);

  return useSyncExternalStore(controller.subscribe, getSelected, getSelected);
}
